import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Plus, 
  Edit, 
  Phone,
  Loader, 
  Trash2, 
  ArrowUpDown, 
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight 
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useInterviewers } from '../context/InterviewerContext';
import FeedbackDrawer from './feedback/FeedbackDrawer'

export default function Dashboard() {
  const { interviewers, isLoading, deleteInterviewer } = useInterviewers();
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggleExpand = (id: number) => {
    setExpandedId(expandedId === id ? null : id);
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this interviewer? This action cannot be undone.')) {
      setDeletingId(id);
      const toastId = toast.loading('Deleting interviewer...');
      try {
        await deleteInterviewer(id);
        toast.success('Interviewer deleted successfully', { id: toastId });
        // Deletion is handled in the context, no need to manually update state
      } finally {
        setDeletingId(null);
      }
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Interviewers</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage and monitor your AI interviewers
          </p>
        </div>
        <Link
          to="/create"
          className="btn btn-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          New Interviewer
        </Link>
      </div>

      {interviewers.length === 0 ? (
        <div className="card p-12 text-center">
          <h3 className="mt-2 text-sm font-semibold text-gray-900">No interviewers</h3>
          <p className="mt-1 text-sm text-gray-500">Get started by creating a new AI interviewer.</p>
          <div className="mt-6">
            <Link
              to="/create"
              className="btn btn-primary"
            >
              <Plus className="h-4 w-4 mr-2" />
              New Interviewer
            </Link>
          </div>
        </div>
      ) : (
        <div className="card">
          <div className="relative w-full overflow-auto">
            <table className="w-full caption-bottom text-sm">
              <thead>
                <tr className="border-b transition-colors hover:bg-muted/50">
                  <th className="table-header" onClick={() => handleSort('name')}>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center">
                        Name
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </th>
                  <th className="table-header" onClick={() => handleSort('company')}>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center">
                        Company
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </th>
                  <th className="table-header" onClick={() => handleSort('interviews_count')}>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center">
                        Calls
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </th>
                  <th className="table-header" onClick={() => handleSort('consents_count')}>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center">
                        Consents
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </th>
                  <th className="table-header" onClick={() => handleSort('phone_calls_count')}>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center">
                        Phone Calls
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </th>
                  <th className="table-header" onClick={() => handleSort('last_active')}>
                    <div className="flex items-center space-x-2">
                      <button className="flex items-center">
                        Last Active
                        <ArrowUpDown className="ml-2 h-4 w-4" />
                      </button>
                    </div>
                  </th>
                  <th className="table-header">
                    <div className="flex items-center justify-center">Actions</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {interviewers.map((interviewer) => (
                  <React.Fragment key={interviewer.id}>
                    <tr className="border-b transition-colors hover:bg-muted/50">
                      <td className="p-4">
                        <Link 
                          to={`/edit/${interviewer.id}`}
                          className="font-medium text-brand-primary hover:underline cursor-pointer"
                        >
                          {interviewer.name}
                        </Link>
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {interviewer.company}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {interviewer.calls_count}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {interviewer.consents_count}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {interviewer.phone_calls_count}
                      </td>
                      <td className="p-4 text-muted-foreground">
                        {interviewer.last_active ? new Date(interviewer.last_active).toLocaleDateString() : '-'}
                      </td>
                      <td className="p-4">
                        <div className="flex items-center justify-center space-x-2">
                          <a 
                            href={interviewer.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary border-2"
                            title="Launch Interview"
                          >
                            <Phone className="h-4 w-4" />
                          </a>
                          <Link to={`/edit/${interviewer.id}`} className="btn btn-secondary">
                            <Edit className="h-4 w-4" />
                          </Link>
                          <button 
                            onClick={() => handleDelete(interviewer.id)} 
                            disabled={deletingId === interviewer.id} 
                            className="btn btn-secondary text-destructive hover:bg-destructive/10 disabled:opacity-50"
                          >
                            {deletingId === interviewer.id ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => toggleExpand(interviewer.id)}
                            className="btn btn-secondary"
                          >
                            {expandedId === interviewer.id ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : (
                              <ChevronDown className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === interviewer.id && (
                      <tr>
                        <td colSpan={6} className="bg-gray-50 px-8 py-4">
                          <FeedbackDrawer interviewerId={interviewer.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-4 py-4 border-t">
            <div className="text-sm text-muted-foreground">
              Showing {interviewers.length} interviewer{interviewers.length !== 1 ? 's' : ''}
            </div>
            <div className="flex items-center space-x-6 lg:space-x-8">
              <div className="flex items-center space-x-2">
                <p className="text-sm font-medium">Rows per page</p>
                <select className="input h-8 w-[70px]">
                  <option value="10">10</option>
                  <option value="20">20</option>
                  <option value="30">30</option>
                  <option value="40">40</option>
                  <option value="50">50</option>
                </select>
              </div>
              <div className="flex w-[100px] items-center justify-center text-sm font-medium">
                Page 1 of 1
              </div>
              <div className="flex items-center space-x-2">
                <button className="btn btn-secondary h-8 w-8 p-0" disabled>
                  <span className="sr-only">Go to previous page</span>
                  <ChevronLeft className="h-4 w-4" />
                </button>
                <button className="btn btn-secondary h-8 w-8 p-0" disabled>
                  <span className="sr-only">Go to next page</span>
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}